import React from "react";
import { Link } from "react-router-dom";
import { Mail, ShoppingBag, Heart, ShieldCheck, Users, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { Editable } from "../components/Editable";
import { useContentApi } from "../context/ContentContext";
import { mailbox } from "../lib/mailboxes";

/**
 * /contact — Public contact directory.
 *
 * Lists every public mailbox the foundation routes inbound email to, with
 * a short description of when to use each. All copy is editable inline by
 * admins via the existing CMS ContentContext + <Editable> pattern (just
 * sign in as admin and click the text to edit, save on blur).
 *
 * Also emits Organization + ContactPoint JSON-LD so Google's knowledge
 * panel picks up the contact info as structured data — this is what
 * surfaces in the right-hand search-results sidebar when people google
 * the foundation's name.
 */

const MAILBOX_CONFIG = [
    {
        key: "accounts",
        icon: Heart,
        contactType: "billing support",
        defaultLabel: "Donations, receipts & financial questions",
        defaultBody: "Use this address for anything related to donations, monthly subscriptions, tax receipts, annual statements, or our public ledger. Replies from our books inbox are typically within one business day.",
    },
    {
        key: "sales",
        icon: ShoppingBag,
        contactType: "sales",
        defaultLabel: "General inquiries, shop orders & community needs",
        defaultBody: "Use this address for shop orders, partnership requests, suggesting a family need, or anything outreach-related. Free-service orders are auto-CC'd here for transparency.",
    },
    {
        key: "volunteers",
        icon: Users,
        contactType: "volunteer support",
        defaultLabel: "Volunteer with us",
        defaultBody: "Want to volunteer time, skills, or coordinate a group? Email here and we'll loop you into the next opportunity. New volunteers also receive an automated welcome email from this address after sign-up.",
    },
    {
        key: "privacy",
        icon: ShieldCheck,
        contactType: "privacy support",
        defaultLabel: "Privacy, data requests & disclosures",
        defaultBody: "Submit a data-access or data-deletion request, ask about how we store information, or report a privacy concern. We respond within 30 days as required by applicable privacy laws.",
    },
];

const Contact = () => {
    const { c } = useContentApi();
    // Build the runtime list by resolving each mailbox address from the CMS.
    const mailboxes = MAILBOX_CONFIG.map((m) => ({ ...m, email: mailbox(c, m.key) }));

    // JSON-LD structured data for Google's knowledge panel.
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Reddog Community Foundation Inc.",
        url: "https://reddogfoundation.org",
        contactPoint: mailboxes.map((m) => ({
            "@type": "ContactPoint",
            email: m.email,
            contactType: m.contactType,
            availableLanguage: "English",
        })),
    };

    return (
        <div className="min-h-screen bg-[#FAF6EE]" data-testid="contact-page">
            <SEO
                title="Contact"
                description="Reach the right team at the Reddog Community Foundation — donations, sales, volunteers, and privacy mailboxes."
                jsonLd={jsonLd}
            />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-black/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20">
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-4">
                        <Editable contentKey="contact.eyebrow" as="span">Reach the right team</Editable>
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1C252E] max-w-3xl leading-[1.05]">
                        <Editable contentKey="contact.title" as="span">Contact us</Editable>
                    </h1>
                    <p className="text-base text-[#4A5568] mt-5 max-w-2xl leading-relaxed">
                        <Editable contentKey="contact.intro" as="span">
                            Every public mailbox lives below — pick the one that matches what you need help with and you&apos;ll skip a forwarding hop. All replies come from staff or volunteers, never an auto-responder.
                        </Editable>
                    </p>
                </div>
            </section>

            {/* Mailbox grid */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid sm:grid-cols-2 gap-6" data-testid="contact-mailbox-grid">
                {mailboxes.map(({ key, email, icon: Icon, defaultLabel, defaultBody }) => (
                    <a
                        key={key}
                        href={`mailto:${email}`}
                        data-testid={`contact-mailbox-${key}`}
                        className="group bg-white border border-black/5 rounded-3xl p-7 hover:border-[#E53935] hover:shadow-lg transition-all"
                    >
                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[#FAF6EE] group-hover:bg-[#E53935] group-hover:text-white flex items-center justify-center transition-colors">
                                <Icon className="w-5 h-5" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-heading text-xl text-[#1C252E] mb-0.5">
                                    <Editable contentKey={`contact.${key}.label`} as="span">{defaultLabel}</Editable>
                                </p>
                                <p className="font-mono text-sm text-[#E53935] truncate">{email}</p>
                            </div>
                        </div>
                        <p className="text-sm text-[#4A5568] leading-relaxed">
                            <Editable contentKey={`contact.${key}.body`} as="span">{defaultBody}</Editable>
                        </p>
                        <p className="text-xs text-[#1C252E] font-medium mt-4 inline-flex items-center gap-1 group-hover:text-[#E53935] transition-colors">
                            Open in mail app
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                        </p>
                    </a>
                ))}
            </section>

            {/* Mailing address + phone */}
            <section className="border-t border-black/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
                    <div className="grid sm:grid-cols-3 gap-8">
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-3">
                                <Editable contentKey="contact.mail.label" as="span">By mail</Editable>
                            </p>
                            <address className="not-italic text-base text-[#1C252E] leading-relaxed">
                                <Editable contentKey="contact.mail.line1" as="div">Reddog Community Foundation Inc.</Editable>
                                <Editable contentKey="contact.mail.line2" as="div">PO Box 231</Editable>
                                <Editable contentKey="contact.mail.line3" as="div">Frenchtown, MT 59834</Editable>
                            </address>
                            <p className="text-xs text-[#4A5568] mt-3">
                                <Editable contentKey="contact.mail.note" as="span">For check donations or store purchases, please make payable to &ldquo;Reddog Community Foundation Inc.&rdquo; — receipt will be mailed within 7 business days.</Editable>
                            </p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-3">
                                <Editable contentKey="contact.phone.label" as="span">By phone</Editable>
                            </p>
                            <a
                                href="tel:+14068020279"
                                data-testid="contact-phone-link"
                                className="text-base text-[#1C252E] leading-relaxed font-semibold hover:text-[#E53935] transition-colors"
                            >
                                <Editable contentKey="contact.phone.number" as="span">(406) 802-0279</Editable>
                            </a>
                            <p className="text-xs text-[#4A5568] mt-3">
                                <Editable contentKey="contact.phone.note" as="span">Reach a board member directly — voicemail if we&rsquo;re away, replies within one business day.</Editable>
                            </p>
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-3">
                                <Editable contentKey="contact.hours.label" as="span">Response times</Editable>
                            </p>
                            <p className="text-base text-[#1C252E] leading-relaxed">
                                <Editable contentKey="contact.hours.body" as="span">
                                    We typically respond within one business day. Donation-related questions (accounts@) are usually answered same-day. Emergency family-aid requests should be flagged in the subject line.
                                </Editable>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="border-t border-black/5 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 text-center">
                    <p className="text-base text-[#4A5568] mb-5">
                        <Editable contentKey="contact.cta.body" as="span">Looking to give back? Skip the inbox and head straight to the action.</Editable>
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/donate" data-testid="contact-cta-donate" className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-7 py-3 font-medium transition-all">
                            <Heart className="w-4 h-4" strokeWidth={1.5} />
                            Donate
                        </Link>
                        <Link to="/about#volunteer" data-testid="contact-cta-volunteer" className="inline-flex items-center gap-2 bg-transparent border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white rounded-full px-7 py-3 font-medium transition-all">
                            <Users className="w-4 h-4" strokeWidth={1.5} />
                            Volunteer
                        </Link>
                        <Link to="/ledger" data-testid="contact-cta-ledger" className="inline-flex items-center gap-2 bg-transparent border border-black/10 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935] rounded-full px-7 py-3 font-medium transition-all">
                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                            Public Ledger
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
